import { defineStore } from "pinia";
import { computed, ref } from "vue";
import api from "@/utils/axios";
import { useAuthStore } from "@/stores/auth";

export type AppNotification = {
  id: number;
  type: string;
  title: string;
  body: string | null;
  link: string | null;
  payload?: Record<string, unknown> | null;
  read_at: string | null;
  created_at: string | null;
  is_read: boolean;
};

export const useNotificationsStore = defineStore("notifications", () => {
  const items = ref<AppNotification[]>([]);
  const unreadCount = ref(0);
  const vapidPublicKey = ref<string | null>(null);
  const loading = ref(false);
  let timer: ReturnType<typeof setInterval> | null = null;
  let seq = 0;

  const auth = useAuthStore();

  const badgeLabel = computed(() =>
    unreadCount.value > 99 ? "99+" : String(unreadCount.value)
  );
  const hasUnread = computed(() => unreadCount.value > 0);

  const refresh = async (full = true) => {
    if (!auth.isAuthenticated) {
      items.value = [];
      unreadCount.value = 0;
      return;
    }
    const mySeq = ++seq;
    loading.value = true;
    try {
      if (full) {
        const res = await api.get("/notifications", { params: { limit: 40 } });
        if (mySeq !== seq) return;
        const data = res.data?.data || {};
        items.value = data.items || [];
        unreadCount.value = Number(data.unread_count || 0);
        if (res.data?.vapid_public_key) {
          vapidPublicKey.value = String(res.data.vapid_public_key);
        }
      } else {
        const res = await api.get("/notifications/unread");
        if (mySeq !== seq) return;
        unreadCount.value = Number(res.data?.data?.unread_count || 0);
        if (res.data?.data?.vapid_public_key) {
          vapidPublicKey.value = String(res.data.data.vapid_public_key);
        }
      }
    } catch {
      /* silencioso */
    } finally {
      if (mySeq === seq) loading.value = false;
    }
  };

  const markRead = async (id: number) => {
    try {
      const res = await api.post(`/notifications/${id}/read`);
      const updated = res.data?.data;
      unreadCount.value = Number(res.data?.unread_count ?? unreadCount.value);
      if (updated) {
        const idx = items.value.findIndex((n) => n.id === id);
        if (idx >= 0) items.value[idx] = { ...items.value[idx], ...updated, is_read: true };
      }
    } catch {
      /* silencioso */
    }
  };

  const markAllRead = async () => {
    try {
      await api.post("/notifications/read-all");
      unreadCount.value = 0;
      items.value = items.value.map((n) => ({
        ...n,
        is_read: true,
        read_at: n.read_at || new Date().toISOString(),
      }));
    } catch {
      /* silencioso */
    }
  };

  const startPolling = () => {
    stopPolling();
    void refresh(true);
    timer = setInterval(() => void refresh(false), 30000);
  };

  const stopPolling = () => {
    if (timer) {
      clearInterval(timer);
      timer = null;
    }
  };

  return {
    items,
    unreadCount,
    vapidPublicKey,
    loading,
    badgeLabel,
    hasUnread,
    refresh,
    markRead,
    markAllRead,
    startPolling,
    stopPolling,
  };
});
